// Logic thuần của editor chip (E5-P2) — tách khỏi component để test được.
// (Import component vào test kéo theo cả antd, mà antd v6 không load nổi dưới jest của react-scripts.)
import type { EventRefBank } from '../../types/film';

export type RefKind = 'crowd' |  'char' | 'look' | 'loc' | 'prop' | 'voice' | 'video';

export const TOKEN_RE = /@\[([^\]\n]*)\]\((char|look|loc|prop|voice|video|crowd):([^)\s]+)\)/g;

export const KIND_META: Record<RefKind, { color: string; label: string; icon: string }> = {
  crowd:{color:'orange',label:'Đám đông nền',icon:'👥'},
  char: { color: 'blue', label: 'Nhân vật', icon: '👤' },
  look: { color: 'purple', label: 'Trang phục', icon: '👗' },
  loc: { color: 'green', label: 'Bối cảnh', icon: '📍' },
  prop: { color: 'orange', label: 'Đạo cụ', icon: '🎯' },
  voice: { color: 'cyan', label: 'Giọng', icon: '🎙️' },
  // 261010 UI2 (L6a): video tham chiếu cấp event → `@Video1` (CHỈ engine 2.5, tối đa 1, đứng cuối refs).
  video: { color: 'red', label: 'Video tham chiếu', icon: '🎞️' },
};

/** Màu AntD → hex, để tô viền/nền chip bằng CSS thuần (chip là DOM tự dựng, không phải <Tag>). */
export const COLOR_HEX: Record<string, string> = {
  blue: '#1677ff', purple: '#722ed1', green: '#52c41a', orange: '#fa8c16', cyan: '#13c2c2', red: '#cf1322',
};

export interface RefChoice { kind: RefKind; id: string; label: string; previewUrl?: string | null; ready: boolean }

/**
 * Gộp bank thành một danh sách chọn phẳng. `ready=false` = chưa có ảnh/asset ⇒ chip sẽ không thành @Image.
 *
 * 260927 mục J: chip nhân vật xét sẵn sàng/preview theo ĐÚNG ảnh engine đang gửi — AVIS (mặc định, `engine`
 * rỗng/undefined cũng tính là AVIS vì đó là mặc định của `videoProvider()`) dùng ẢNH NGƯỜI MẶC bộ chính
 * (`hasWornLook`/`wornLookPreviewUrl`), BytePlus giữ đường cũ (`hasDC`/`previewUrl` = mặt/DC).
 */
export function buildRefChoices(bank?: EventRefBank, engine?: 'AVIS' | 'BYTEPLUS', extra: RefChoice[] = []): RefChoice[] {
  const isByteplus = engine === 'BYTEPLUS';
  const out: RefChoice[] = [];
  for (const c of bank?.characters || []) {
    if(c.kind==='CROWD'){if(c.id)out.push({kind:'crowd',id:c.id,label:c.name,previewUrl:c.groupRefUrl,ready:!!c.groupRefUrl});continue;}
    if (c.id) {
      const ready = isByteplus ? !!c.hasDC : !!c.hasWornLook;
      const previewUrl = isByteplus ? c.previewUrl : (c.wornLookPreviewUrl || c.previewUrl);
      out.push({ kind: 'char', id: c.id, label: c.name, previewUrl, ready });
    }
    if (c.id && c.hasVoice) out.push({ kind: 'voice', id: c.id, label: `${c.name} (giọng)`, ready: true });
  }
  for (const l of bank?.looks || []) out.push({ kind: 'look', id: l.id, label: l.name, previewUrl: l.previewUrl, ready: true });
  for (const l of bank?.locations || []) {
    if (l.id) out.push({ kind: 'loc', id: l.id, label: l.name, previewUrl: l.plateUrl, ready: !!l.hasPlate });
  }
  for (const p of bank?.props || []) out.push({ kind: 'prop', id: p.id, label: p.name, previewUrl: p.previewUrl, ready: !!p.hasSheet });
  return [...out, ...extra];
}

export type Piece =
  | { type: 'text'; text: string }
  | { type: 'token'; kind: RefKind; id: string; label: string; raw: string };

export function splitPieces(text: string): Piece[] {
  const out: Piece[] = [];
  const src = String(text || '');
  const re = new RegExp(TOKEN_RE.source, 'g'); // bản sao có state riêng — TOKEN_RE là /g dùng chung
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) out.push({ type: 'text', text: src.slice(last, m.index) });
    out.push({ type: 'token', kind: m[2] as RefKind, id: m[3], label: m[1].trim(), raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < src.length) out.push({ type: 'text', text: src.slice(last) });
  return out;
}

export const buildToken = (kind: RefKind, id: string, label: string): string =>
  `@[${String(label).replace(/[\]\n]/g, ' ').trim()}](${kind}:${id})`;

/**
 * DOM (contentEditable) → text để lưu.
 *
 * Chip là phần tử `contentEditable=false` mang sẵn token ở `data-token`, nên trình duyệt xử lý nó như
 * MỘT KHỐI — Backspace xoá nguyên chip chứ không xé lẻ token thành rác giữa prompt.
 * ⚠️ Gặp chip thì lấy `data-token` rồi DỪNG, không duyệt vào trong: bên trong là ảnh + nhãn hiển thị,
 * duyệt tiếp là nhãn lọt ra thành text thừa nằm cạnh token.
 */
export function domToText(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === 3 /* TEXT_NODE */) { out += node.textContent || ''; return; }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return;
    const el = node as HTMLElement;
    const tok = el.getAttribute('data-token');
    if (tok) { out += tok; return; }
    if (el.tagName === 'BR') { out += '\n'; return; }
    // Trình duyệt bọc mỗi dòng mới trong <div>/<p> khi gõ Enter.
    if ((el.tagName === 'DIV' || el.tagName === 'P') && out && !out.endsWith('\n')) out += '\n';
    el.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return out;
}
